import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TargetIcon, TrendingUpIcon, BarChart3Icon, CheckCircleIcon, ArrowRightIcon, StarIcon, ChevronDownIcon, BuildingIcon, ZapIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function AdCampaignsPage() {
  const faqs = [{
    question: 'Why should I advertise on Yelp instead of Google?',
    answer: 'Yelp users have exceptionally high purchase intent. When someone searches for a plumber or roofer on Yelp, they are usually looking to hire someone that same day. While Google is great for broad searches, Yelp is the bottom of the funnel for local services.'
  }, {
    question: 'What does it mean to be a Yelp Advertising Premier Partner?',
    answer: "As a Yelp Advertising Premier Partner, we have direct access to Yelp representatives, priority support, and exclusive insights into bidding strategies and platform updates that regular advertisers don't get. Premier is Yelp's top partner tier, reserved for agencies with proven performance."
  }, {
    question: 'Do I need a large budget to start?',
    answer: 'No. We work with businesses of all sizes to determine a starting budget that makes sense for your specific industry and local market competition. We focus on maximizing ROI, not just spending money.'
  }, {
    question: 'How do you track if the ads are actually working?',
    answer: "We implement full-funnel tracking. We don't just track clicks or impressions; we track actual phone calls, messages, and form fills generated from the ads, tying them directly back to your revenue."
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-413" data-mp="1embimk5ivvxz">
        <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" onClick={() => setIsOpen(!isOpen)} data-id="element-414" data-mp="1emtirrvb2ub6">
          <span className="text-lg font-bold text-white group-hover:text-red-500 transition-colors" data-id="element-415" data-mp="15syh4tte7m0a">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-red-500' : ''}`} data-id="element-416" data-mp="19syueb4r9r5r" />
        </button>
        <AnimatePresence data-id="element-417" data-mp="10sah5in6rutx">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-418" data-mp="mw70ks58jkzx">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-419" data-mp="5mcl6qeq7fhx">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="bg-viridia-black min-h-screen" data-id="element-420" data-mp="em7z32zo1oln">
      <SEOHead title="Yelp Advertising Premier Partner | AI Ad Management | Viridia Analytics Phoenix" description="Expert Yelp Ads and AI ad management for Phoenix local businesses. As a Yelp Advertising Premier Partner, our marketing agency maximizes your ad spend." canonical="/ad-campaigns" keywords="Yelp Ads management Phoenix, Yelp Ads partner agency, AI advertising, AI ad management, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers, local business advertising, ad campaign optimization" faq={faqs} data-id="element-421" data-mp="elg4nvht1wwy" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-422" data-mp="2z5x5kckgqtqs">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-423" data-mp="31l3ogpmq0act">
          <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-red-500/20 blur-[120px] animate-pulse-slow" data-id="element-424" data-mp="3ajfk562g1yc4"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-425" data-mp="3fh78jq3vr2bk">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-426" data-mp="2hvt591nrn79y">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-427" data-mp="2mu4fvelvxlt8">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-428" data-mp="2rt7n95pc844d">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-sm font-semibold tracking-wide mb-6" data-id="element-429" data-mp="242lhjl2t2vxj">
                  PAID ADVERTISING
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-430" data-mp="1rliyupxp9n3z">
                  Your Yelp Ads{' '}
                  <span className="text-red-500" data-id="element-431" data-mp="1hn36kjmxs7sw">Partner Agency</span>
                </h1>
                <p className="text-lg text-gray-400 mb-4 leading-relaxed" data-id="element-432" data-mp="1dkq3k2zkqnzt">
                  Leverage our insider expertise from former Yelp employees to
                  drive qualified leads. We understand CPC optimization,
                  competitive bidding, and seasonality trends that maximize ROI
                  on Yelp for local service businesses.
                </p>
                <p className="text-lg text-red-500 font-medium mb-8 leading-relaxed" data-id="element-433" data-mp="yu92ikquq03g">
                  In the last 30 days, our clients generated 380+ Yelp leads and
                  booked 57 AI estimates across two campaigns.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-434" data-mp="f98xulh920l6">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-435" data-mp="k951ukpy5s0q">
                    Book a Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-436" data-mp="yzc7u0yrpikj">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration */}
            <div className="relative h-[400px] lg:h-[500px] hidden md:block" data-id="element-437" data-mp="1hv2ew2lk555a">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="absolute inset-0 flex items-center justify-center" data-id="element-438" data-mp="1mpf0ojaiask7">
                {/* Central Yelp Partner Badge */}
                <motion.div className="relative z-30 flex flex-col items-center" animate={{
                y: [0, -15, 0]
              }} transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-439" data-mp="1d4bgorpvmbs4">
                  <img src="https://cdn.magicpatterns.com/uploads/rbq8W356ty5oybkr7qHyKz/yelp-premiere-partner-1.png" alt="Yelp Advertising Premier Partner" className="w-80 h-80 object-contain drop-shadow-[0_0_40px_rgba(239,68,68,0.4)]" data-id="element-440" data-mp="2ygcxqi2l7qo7" />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORMS SECTION */}
      <section className="py-24 bg-viridia-light relative" data-id="element-441" data-mp="10rqxcoh9hg6x">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-442" data-mp="15l24yrtwe0zw">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-443" data-mp="1ajwqi1j923np">
            <div data-id="element-444" data-mp="1figavxi737dy">
              <ScrollReveal data-id="element-445" data-mp="1fhygl6f3f38o">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-500/10 text-red-500 text-sm font-semibold tracking-wide uppercase mb-6 border border-red-500/20" data-id="element-446" data-mp="1fh5bsazp2nt6">
                  PLATFORM EXPERTISE
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-447" data-mp="3lbg2nuqbofah">
                  Why Yelp Ads?
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-448" data-mp="3qbkjxmz21e1r">
                  Yelp is where high-intent customers go when they are ready to
                  hire a local service professional. We don't just set and
                  forget. We actively manage your campaigns using insider
                  knowledge to maximize your return on ad spend.
                </p>
                <div className="space-y-6" data-id="element-449" data-mp="342vlqjakvjzn">
                  {['TFP Landscaping: 242 leads, 36.4k impressions in 30 days', 'Finish Coat Painting: 138 leads, 33.2k impressions in 30 days', 'Optimize Cost-Per-Click (CPC) to stretch your budget', 'Leverage our Partner Agency priority support'].map((feature, index) => <div key={index} className="flex items-start" data-id="element-450" data-mp="3npbi6931qq5x" data-mpi={index}>
                      <CheckCircleIcon className="w-6 h-6 text-red-500 mr-4 flex-shrink-0 mt-0.5" data-id="element-451" data-mp="2gd1ag65wq3hi" />
                      <span className="text-gray-700 font-medium text-lg" data-id="element-452" data-mp="2caeoglvx3cs8">
                        {feature}
                      </span>
                    </div>)}
                </div>
              </ScrollReveal>
            </div>

            <div className="relative" data-id="element-453" data-mp="nngn8294h60">
              <ScrollReveal direction="left" data-id="element-454" data-mp="ecxqahfhqr5e">
                <div className="bg-white rounded-2xl p-10 shadow-xl border-t-4 border-red-500 relative overflow-hidden" data-id="element-455" data-mp="ees8vp3ai76b">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-bl-full -z-10" data-id="element-456" data-mp="9kebxi8zbrzj"></div>
                  <div className="w-16 h-16 rounded-xl bg-red-500/10 flex items-center justify-center text-red-500 mb-8" data-id="element-457" data-mp="od83w35fu0l3">
                    <StarIcon className="w-8 h-8 fill-current" data-id="element-458" data-mp="xcldqwxh4r6c" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-459" data-mp="sd8fzh52dzoz">
                    Targeted Local Advertising
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg mb-8" data-id="element-460" data-mp="2z47y4fmv1ntu">
                    Reach customers already looking for businesses like yours on
                    Yelp. Our team includes former Yelp insiders who know
                    exactly how to maximize your ad spend on the platform.
                  </p>
                  <Link to="/contact" className="inline-flex items-center text-red-500 font-bold hover:text-red-600 transition-colors text-lg" data-id="element-461" data-mp="1eni7k744d6nd">
                    Start Dominating Yelp{' '}
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-462" data-mp="qvjtjfdacx6j" />
                  </Link>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* The Real Problem with Ads */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-463" data-mp="hwy36y1kodv7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-464" data-mp="lxi0c1m5bhhc">
          <ScrollReveal data-id="element-465" data-mp="2c9fo3yyxv1wo">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-466" data-mp="2ca7ix7kpovlw">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-467" data-mp="2gahlea9ap3w0">
                Ad Spend is Wasted Without Follow-Up
              </h2>
              <p className="text-xl text-gray-400" data-id="element-468" data-mp="3p99bdbs2mxvs">
                Generating clicks is easy. Turning those clicks into booked jobs
                is hard.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-469" data-mp="3g946p9e9idlb">
            <ScrollReveal delay={0.1} data-id="element-470" data-mp="3b98c20rgfyif">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full" data-id="element-471" data-mp="3ba9ni6cdun0o">
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-472" data-mp="bumi56qa1zfm">
                  The Problem
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-473" data-mp="3083n6dkmsxk">
                  Most agencies focus solely on getting you more traffic. But if
                  your team is busy on a jobsite and takes hours to respond to a
                  new lead, that lead has already called your competitor. You
                  paid for the click, but lost the job.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-474" data-mp="y20eig4vcvt9">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full" data-id="element-475" data-mp="y31mucui347a">
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-476" data-mp="130jywc3tdzji">
                  The Solution
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-477" data-mp="181r6ob5yiyq6">
                  We pair our ad management with our AI Lead Conversion system.
                  Every lead generated by your ads receives an instant response,
                  ensuring your ad budget actually turns into booked
                  appointments.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-478" data-mp="1vofufdhndlep">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-479" data-mp="1zrlxkc9gqwgy">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-480" data-mp="24pnht0iml1u0">
            <ScrollReveal data-id="element-481" data-mp="24pxa4ur57l2v">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-482" data-mp="1r2x0hyxhwmgv">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-483" data-mp="1n1v37nn52khj">
                Our ad management services are designed for home service
                businesses that want to dominate their local market on Yelp.
              </p>
              <ul className="space-y-4" data-id="element-484" data-mp="1vu6rp3yozxuv">
                <li className="flex items-start gap-3" data-id="element-485" data-mp="1842pn115k65z">
                  <BuildingIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-486" data-mp="184ksigiblimv" />
                  <div data-id="element-487" data-mp="143sivm5jdbq2">
                    <h4 className="font-bold text-white" data-id="element-488" data-mp="144aau6l613s6">Local Contractors</h4>
                    <p className="text-sm text-gray-400" data-id="element-489" data-mp="1i31mlmck2dd5">
                      HVAC, painters, landscapers, window installers, plumbers,
                      and roofers looking for high-intent local leads.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-490" data-mp="2oj3vk225ps43">
                  <ZapIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-491" data-mp="2okor9v07jh3j" />
                  <div data-id="element-492" data-mp="a98feahm8e4f">
                    <h4 className="font-bold text-white" data-id="element-493" data-mp="f9mnttn4pq2z">
                      Businesses Ready to Scale
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-494" data-mp="1an1isn39n9z">
                      If you have the capacity to take on more jobs and want a
                      predictable lead generation engine.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-495" data-mp="xz2pwlegpqli">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-496" data-mp="y28fe1r06uhr">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-497" data-mp="k8h5utd9vam1">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-498" data-mp="p5hcb5e1afdo">
                  <Link to="/ai-lead-conversion" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-499" data-mp="1cwdaqlczw8tw">
                    <div className="font-bold text-white mb-1" data-id="element-500" data-mp="1rjc0mbw7ev6i">
                      AI Lead Conversion
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-501" data-mp="1hlxkyei3uaf9">
                      Ensure every ad click gets an instant response.
                    </div>
                  </Link>
                  <Link to="/web-design-seo" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-502" data-mp="1coxew3l997o5">
                    <div className="font-bold text-white mb-1" data-id="element-503" data-mp="yvfw4l0e7yz6">
                      Conversion Websites
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-504" data-mp="txebvwr8dtm4">
                      Send your ad traffic to a site built to convert.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-505" data-mp="2wi8nwtrjpf5t">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-506" data-mp="30ju4oq77adla">
          <ScrollReveal data-id="element-507" data-mp="35j6rjay2nknv">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-508" data-mp="35kq52tscfhly">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-509" data-mp="2cvjau6zx9e6i">
          <ScrollReveal delay={0.2} data-id="element-510" data-mp="2csy87frsuu7p">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-511" data-mp="2htktwrxcldhz">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-512" data-mp="2mrme5g6ifiv1" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-513" data-mp="1z2uqn23v7ool">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-514" data-mp="sg0p4d8t9we7">
          <ScrollReveal data-id="element-515" data-mp="si52rmwii9ro">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-516" data-mp="nhh4qh9v8sny">
              Stop wasting ad spend
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-517" data-mp="11i1obfnlusii">
              Book a strategy call to see how we can optimize your ad campaigns
              and ensure every lead is followed up with instantly.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-viridia text-white hover:bg-viridia-dark transition-all duration-300" data-id="element-518" data-mp="1bd1va1e3q2jq">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-519" data-mp="1g6xs30impjdb" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}